import { useEffect } from "react";

import cookies from "js-cookie";
import { shallow } from "zustand/shallow";
import { useStoreWithEqualityFn } from "zustand/traditional";
import { createStore } from "zustand/vanilla";

import { terminateSubscriptions } from "../../lib/apollo/subscriptions";
import {
  SignedOutError,
  endSession,
  establishSession,
  fetchBrowserToken,
} from "@/auth/browser-token";
import { LEGACY_TOKEN_COOKIE, SIGNED_IN_COOKIE } from "@/auth/session-cookies";
import { createDeviceId } from "@/auth/utils";
import { useApolloClient } from "@apollo/client";

type AuthStore = {
  isReady: boolean;
  setReady: () => void;
  /** WHO-6344: the 5-minute browser token, in memory only. */
  token: string | null;
  /** Whether a web session exists (the httpOnly cookie behind it). */
  signedIn: boolean;
  /** After sign-in: hand over the login mutation's token. */
  setToken: (token: string) => void;
  deviceId: string;
  setDeviceId: (deviceId: string) => void;
  userId: string | null;
  setUserId: (userId: string) => void;
  reset: () => void;
};

const COOKIE_OPTIONS = {
  path: "/", // The cookie is available in all pages.
  expires: 365, // 365 days (1 year)
  sameSite: "lax", // lax prevents the cookie from being sent on cross-site requests
} as const;

/**
 * Generate an anonymous User ID if it doesn't exist yet.
 * This is used to identify anonymous users. It should be passed in header to the API.
 */
if (!cookies.get("device_id")) {
  const deviceId = createDeviceId();
  cookies.set("device_id", deviceId, COOKIE_OPTIONS);
}

/**
 * Remove legacy anonymous User ID from cookies.
 */
if (cookies.get("user_id")?.startsWith("anonymous")) {
  cookies.remove("user_id");
}

/**
 * Get the initial session state, userId and deviceId from the cookies.
 * The session itself is an httpOnly cookie; `wh_signed_in` only says it
 * exists. A pre-WHO-6344 `token` cookie counts until `/api/session/token`
 * migrates it.
 */
const initialSignedIn =
  cookies.get(SIGNED_IN_COOKIE) === "1" || !!cookies.get(LEGACY_TOKEN_COOKIE);
const initialUserId = cookies.get("user_id") ?? null;
const initialDeviceId = cookies.get("device_id") ?? "";

/**
 * Zustand store for authentication.
 */
export const authStore = createStore<AuthStore>(set => ({
  /**
   * Whether the authentication store is ready.
   */
  isReady: false,
  /**
   * Set the authentication store to ready.
   */
  setReady: () => {
    set({ isReady: true });
  },
  /**
   * The browser token. Filled by `getBrowserToken`.
   */
  token: null,

  signedIn: initialSignedIn,

  /**
   * Exchange the login token for an httpOnly web session. The login token is
   * revoked by the exchange and never stored.
   * @param token - The token the login or signup mutation returned.
   */
  setToken: (token: string) => {
    set({ signedIn: true });
    establishSession(token)
      .then(browserToken => {
        if (browserToken) {
          set({ token: browserToken.accessToken });
        }
      })
      .catch(error => {
        if (error instanceof SignedOutError) {
          authStore.getState().reset();
        }
      });
  },

  /**
   * Sign out: drop the browser token and the userId, and end the session.
   */
  reset: () => {
    set({ token: null, signedIn: false, userId: null });
    void endSession();
  },

  /**
   * The User ID of the authenticated user.
   */
  userId: initialUserId,

  /**
   * Set the User ID of the authenticated user.
   * @param userId - The User ID.
   */
  setUserId: (userId: string) => {
    set({ userId });
  },

  /**
   * The Device ID of the authenticated or anonymous user.
   */
  deviceId: initialDeviceId,

  /**
   * Set the Device ID of the authenticated or anonymous user.
   * @param deviceId - The Device ID.
   */
  setDeviceId: (deviceId: string) => {
    set({ deviceId });
  },
}));

/**
 * The current browser token for a gateway call, refreshed when it is about to
 * expire. Null when signed out (or when the session route is unreachable).
 */
export const getBrowserToken = async (): Promise<string | null> => {
  if (!authStore.getState().signedIn) {
    return null;
  }

  try {
    const browserToken = await fetchBrowserToken();
    const token = browserToken?.accessToken ?? null;

    if (token && token !== authStore.getState().token) {
      authStore.setState({ token });
    }

    return token;
  } catch (error) {
    if (error instanceof SignedOutError) {
      authStore.getState().reset();
    }

    return null;
  }
};

/**
 * Hook to use the auth store. This is a wrapper around Zustand's `useStore` hook.
 * @returns The auth store.
 */
export const useAuthStore = () => {
  const client = useApolloClient();

  useEffect(() => {
    /**
     * Subscribe to the auth store.
     * @param token - The token.
     * @returns The unsubscribe function.
     */
    const unsubscribe = authStore.subscribe((state, prevState) => {
      /**
       * Update the Device ID in the cookie if it has changed.
       */
      if (state.deviceId !== prevState.deviceId) {
        // Update the Device ID in the cookie.
        if (state.deviceId) {
          cookies.set("device_id", state.deviceId, COOKIE_OPTIONS);
        } else {
          cookies.remove("device_id");
        }
      }

      /**
       * Update the User ID in the cookie if it has changed.
       */
      if (state.userId !== prevState.userId) {
        // Update the User ID in the cookie.
        if (state.userId) {
          cookies.set("user_id", state.userId, COOKIE_OPTIONS);
        } else {
          cookies.remove("user_id");
        }
      }

      /**
       * Reset the Apollo store on sign-out, and reconnect the subscriptions
       * whenever the member signs in or out. A browser token refresh needs
       * neither: the WebSocket authenticates once, at connect.
       */
      if (state.signedIn !== prevState.signedIn) {
        if (!state.signedIn) {
          client.resetStore();
        }

        terminateSubscriptions();
      }
    });

    /**
     * Unsubscribe from the store when the component unmounts.
     */
    return unsubscribe;
  }, [client]);

  // Return the store with a shallow equality function.
  // This prevents unnecessary re-renders.
  // See:
  // - https://github.com/pmndrs/zustand/discussions/1937#discussioncomment-6610415
  return useStoreWithEqualityFn(authStore, state => state, shallow);
};
